import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { db } from "../firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import MenuNav from "../components/Menunav";
import MobileMenu from "../components/MobileMenu";
import FooterOutils from "../components/Footer";
import { Helmet } from "react-helmet-async";

export default function HomeOutils() {
  const [recents, setRecents] = useState([]);

  // 👉 Chargement des items
  const [loadingItems, setLoadingItems] = useState(true);

  // 👉 Nombre d'items visibles + flag "Voir plus"
  const [visibleCount, setVisibleCount] = useState(8);
  const [hasClickedVoirPlus, setHasClickedVoirPlus] = useState(false);

  // 👉 Slides dynamiques
  const [slides, setSlides] = useState([]);
  const [loadingSlides, setLoadingSlides] = useState(true);

  // 👉 Fallback : slides en dur
  const defaultSlides = [
    {
      type: "image",
      src: "/1.png",
      alt: "Débroussailleuse en action dans un champ",
      title: "",
      text: "Des équipements performants pour vos travaux agricoles",
      cta: { label: "🔍 Voir le catalogue", to: "/catalogue" },
    },
    {
      type: "image",
      src: "/2.png",
      alt: "Démonstration d'un pulvérisateur",
      title: "",
      text: "",
      cta: { label: "Découvrir", to: "/catalogue-outils?cat=Pulvérisateurs" },
    },
    {
      type: "image",
      src: "/3.png",
      alt: "Motoculteur pour petits exploitants",
      title: "",
      text: "",
      cta: {
        label: "",
        to: "/outil/pulvérisateurs-agricoles-sac-à-dos-pulvérisateur-de-grande-capacité-agriculture-pulvérisateur-de-jardin-à-batterie-de-16-litres-1754600776271",
      },
    },
  ];

  /* 🔹 Produits récents */
  useEffect(() => {
    const fetchItems = async () => {
      setLoadingItems(true);
      try {
        const snapshot = await getDocs(collection(db, "items"));
        const data = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((item) => item.disponible);

        setRecents(data);

        if (data.length < 8) {
          setVisibleCount(data.length);
        }
      } catch (error) {
        console.error("Erreur chargement items:", error);
      } finally {
        setLoadingItems(false);
      }
    };
    fetchItems();
  }, []);

  /* 🔹 Slides du hero (Firestore) */
  useEffect(() => {
    const fetchSlides = async () => {
      setLoadingSlides(true);
      try {
        const slidesRef = collection(db, "outils_heroSlides");
        const qSlides = query(slidesRef, orderBy("order", "asc"));
        const snap = await getDocs(qSlides);

        const data = snap.docs.map((d) => {
          const s = d.data();
          return {
            id: d.id,
            type: s.type || "image",
            src: s.src || "",
            alt: s.alt || "",
            title: s.title || "",
            text: s.text || "",
            cta:
              s.ctaLabel || s.ctaTo
                ? { label: s.ctaLabel || "", to: s.ctaTo || "#" }
                : null,
            interval: s.interval || 3000,
            isActive: s.isActive ?? true,
          };
        });

        const activeSlides = data.filter((s) => s.isActive);

        if (activeSlides.length > 0) {
          setSlides(activeSlides);
        } else {
          setSlides(defaultSlides);
        }
      } catch (e) {
        console.error("Erreur chargement slides:", e);
        setSlides(defaultSlides);
      } finally {
        setLoadingSlides(false);
      }
    };

    fetchSlides();
  }, []);

  const carouselInterval = slides[0]?.interval || 3000;

  // 👉 Handler "Voir plus"
  const handleVoirPlus = () => {
    setVisibleCount((prev) => {
      const next = prev + 8;
      return next > recents.length ? recents.length : next;
    });
    setHasClickedVoirPlus(true);
  };

  const displayedItems = recents.slice(0, visibleCount);

  return (
    <>
      <MenuNav />
      <MobileMenu />

      {/* ✅ SEO statique pour la page d’accueil outils */}
      <Helmet>
        <title>Outils agricoles – Vente & équipements | LA F.A.P.E</title>
        <meta
          name="description"
          content="Découvrez une sélection d’outils agricoles : tracteurs, motoculteurs, pulvérisateurs, semoirs et équipements professionnels pour vos travaux agricoles."
        />
        <meta
          name="keywords"
          content="outils agricoles, tracteur, motoculteur, pulvérisateur, semoir, équipement agricole, LA F.A.P.E"
        />

        <meta property="og:title" content="Outils agricoles – LA F.A.P.E" />
        <meta
          property="og:description"
          content="Une gamme complète d’outils et machines agricoles adaptés à vos besoins."
        />
        <meta
          property="og:url"
          content="https://ton-domaine.com/catalogue-outils"
        />
        <meta
          property="og:image"
          content="https://ton-domaine.com/og-outils.jpg"
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* Hero - Carousel */}
      <div
        id="heroCarousel"
        className="carousel slide"
        data-bs-ride="carousel"
        data-bs-interval={carouselInterval}
      >
        {/* Indicateurs */}
        <div className="carousel-indicators">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              data-bs-target="#heroCarousel"
              data-bs-slide-to={i}
              className={i === 0 ? "active" : ""}
              aria-current={i === 0 ? "true" : "false"}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>

        {/* Slides */}
        <div className="carousel-inner">
          {slides.map((s, i) => (
            <div
              className={`carousel-item ${i === 0 ? "active" : ""}`}
              key={s.id || i}
            >
              {s.type === "image" ? (
                <img
                  src={s.src}
                  alt={s.alt}
                  className="d-block w-100"
                  style={{ width: "100%", height: "auto" }}
                  loading="lazy"
                />
              ) : (
                <video
                  className="d-block w-100"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="none"
                  style={{ width: "100%", height: "auto" }}
                >
                  <source src={s.src} type="video/mp4" />
                </video>
              )}

              {(s.title || s.text || s.cta) && (
                <div className="carousel-caption">
                  <div className="caption-box">
                    {s.title && (
                      <h1 className="display-6 fw-bold mb-1">{s.title}</h1>
                    )}
                    {s.text && <p className="mb-2">{s.text}</p>}
                    {s.cta && s.cta.to && (
                      <Link
                        to={s.cta.to}
                        className="btn btn-bridge btn-sm mt-1"
                      >
                        {s.cta.label || "En savoir plus"}
                      </Link>
                    )}
                  </div>
                </div>
              )}

              <div className="hero-overlay" />
            </div>
          ))}
        </div>

        {/* Contrôles */}
        <button
          className="carousel-control-prev"
          type="button"
          data-bs-target="#heroCarousel"
          data-bs-slide="prev"
        >
          <span
            className="carousel-control-prev-icon"
            aria-hidden="true"
          ></span>
          <span className="visually-hidden">Précédent</span>
        </button>
        <button
          className="carousel-control-next"
          type="button"
          data-bs-target="#heroCarousel"
          data-bs-slide="next"
        >
          <span
            className="carousel-control-next-icon"
            aria-hidden="true"
          ></span>
          <span className="visually-hidden">Suivant</span>
        </button>
      </div>

      {/* Catégories populaires */}
      <div className="container my-5">
        <h3 className="mb-4 text-center">🌿 Catégories populaires</h3>
        <div className="row text-center">
          {["Tracteurs", "Motoculteurs", "Pulvérisateurs", "Semoirs", "Autres"].map(
            (cat, i) => (
              <div key={i} className="col-6 col-md-2 mb-3">
                <Link
                  to={`/catalogue-outils?cat=${cat}`}
                  className="text-decoration-none text-dark"
                >
                  <div className="border rounded py-3 shadow-sm h-100">
                    <div className="fs-3">🚜</div>
                    <div>{cat}</div>
                  </div>
                </Link>
              </div>
            )
          )}
        </div>
      </div>

      {/* Produits récents */}
      <div className="container my-5">
        <h3 className="mb-4">🆕 Outils récemment ajoutés</h3>

        <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-3 g-4">
          {loadingItems
            ? // 🔸 Squelettes de chargement
              Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="col">
                  <div className="card h-100">
                    <div className="placeholder-glow">
                      <div
                        className="placeholder col-12"
                        style={{ height: "240px" }}
                      />
                    </div>
                    <div className="card-body">
                      <p className="placeholder-glow mb-2">
                        <span className="placeholder col-8" />
                      </p>
                      <p className="placeholder-glow mb-2">
                        <span className="placeholder col-6" />
                      </p>
                      <p className="placeholder-glow mb-0">
                        <span className="placeholder col-4" />
                      </p>
                    </div>
                  </div>
                </div>
              ))
            : // 🔸 Vrais produits
              displayedItems.map((item) => (
                <div key={item.id} className="col">
                  <Link
                    to={`/outil/${item.slug}`}
                    className="text-decoration-none"
                  >
                    <div className="card h-100">
                      {item.imageUrls?.[0] && (
                        <img
                          src={item.imageUrls[0]}
                          className="card-img-top"
                          alt={item.nom}
                          style={{ height: "480px", objectFit: "cover" }}
                        />
                      )}
                      <div className="card-body">
                        <h6 className="card-title mb-1">{item.nom}</h6>
                        <p className="small text-muted mb-1">{item.modele}</p>
                        <p className="fw-bold text-success mb-0">
                          {item.prix
                            ? "Prix à la demande"
                            : "Prix à la demande"}
                        </p>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
        </div>

        {/* Boutons Voir plus / Voir tout le catalogue */}
        {!loadingItems && recents.length > 0 && (
          <div className="text-center" style={{ marginTop: "20px" }}>
            {visibleCount < recents.length && (
              <button
                type="button"
                className="btn btn-outline-success me-2"
                style={{ boxShadow: "0.5 15px 16px rgba(0, 0, 0, 0.1)" }}
                onClick={handleVoirPlus}
              >
                Voir plus
              </button>
            )}

            {hasClickedVoirPlus && (
              <Link
                to="/catalogue"
                className="btn btn-link text-success fw-semibold"
              >
                Voir tout le catalogue
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Pourquoi nous choisir */}
      <div className="bg-light py-5">
        <div className="container">
          <h3 className="mb-4 text-center">
            Pourquoi choisir notre plateforme ?
          </h3>
          <div className="row text-center">
            <div className="col-md-4 mb-3">
              <div className="fs-1">✅</div>
              <p className="fw-bold mb-1">Qualité garantie</p>
              <p>Des outils testés et sélectionnés pour leur fiabilité.</p>
            </div>
            <div className="col-md-4 mb-3">
              <div className="fs-1">🚚</div>
              <p className="fw-bold mb-1">Livraison rapide</p>
              <p>Nous livrons partout avec des délais compétitifs.</p>
            </div>
            <div className="col-md-4 mb-3">
              <div className="fs-1">📞</div>
              <p className="fw-bold mb-1">Support dédié</p>
              <p>Un accompagnement personnalisé pour chaque client.</p>
            </div>
          </div>
        </div>
      </div>

      <FooterOutils />
    </>
  );
}
